import { HttpErrorResponse } from '@angular/common/http'
import { Component, signal } from '@angular/core'
import { AxiosResponse } from 'axios'
import saveAs from 'file-saver'
import { ExportIncident } from '../../../models/incident.interface'
import {
    FormBuilder,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms'
import { QueryGet } from '../../../shared/query-types'
import { MatDialogRef } from '@angular/material/dialog'
import { IncidentService } from '../../../services/incident/incident.service'
import { ToastrService } from 'ngx-toastr'
import { DatePipe, NgIf } from '@angular/common'
import { LoadingService } from '../../../services/loadingService/loading.service'
import { DateAdapter } from '@angular/material/core'
import { months } from '../../incident-observatorio/incident-form/incident.definitions'
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field'
import { MatIcon } from '@angular/material/icon'
import { MatChipsModule } from '@angular/material/chips'
import {
    CdkDrag,
    CdkDragDrop,
    CdkDropList,
    DragDropModule,
    moveItemInArray,
} from '@angular/cdk/drag-drop'
import {
    MatDatepicker,
    MatDatepickerModule,
    MatDatepickerToggle,
} from '@angular/material/datepicker'
import { MatInputModule } from '@angular/material/input'
import { FormUtils } from '../../../utils'

interface IncidentField {
    key: string
    label: string
}

@Component({
    selector: 'app-export-incident',
    standalone: true,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormField,
        MatLabel,
        MatError,
        MatIcon,
        MatChipsModule,
        CdkDropList,
        CdkDrag,
        MatDatepickerModule,
        MatDatepicker,
        MatDatepickerToggle,
        MatInputModule,
        NgIf,
        DragDropModule,
    ],
    providers: [DatePipe],
    templateUrl: './export-incident.component.html',
    styleUrl: './export-incident.component.css',
})
export class ExportIncidentComponent {
    errorMsg: string[] | null = null
    months = months
    currentYears: number[] = []
    date = new Date().toISOString().split('T')[0]

    readonly fields = signal<IncidentField[]>([
        { key: 'numero_caso', label: 'Número de caso' },
        { key: 'hecho_victimizante', label: 'Hecho victimizante' },
        { key: 'tipo_violencia', label: 'Tipo de violencia' },
        { key: 'categoria_violencia', label: 'Categoría' },
        { key: 'victimas', label: 'Nombre_victima' },
        { key: 'tipo_documento', label: 'tipo de documento' },
        { key: 'numero_documento', label: 'numero de documento' },
        { key: 'edad', label: 'Edad' },
        { key: 'sexo', label: 'Sexo' },
        { key: 'ocupacion', label: 'Ocupacion' },
        { key: 'estado_civil', label: 'Estado civil' },
        { key: 'etnia', label: 'Etnia' },
        { key: 'municipio_hecho', label: 'Municipio' },
        { key: 'plan_vida_hecho', label: 'plan de Vida' },
        { key: 'resguardo_hecho', label: 'Territorio' },
        { key: 'vereda_hecho', label: 'Vereda' },
        { key: 'mes', label: 'Mes de reporte' },
        { key: 'fecha', label: 'Fecha de reporte' },
        { key: 'hora', label: 'Hora de reporte' },
        { key: 'description', label: 'Relato de los hechos' },
        { key: 'municipio_nacimiento_victima', label: 'Municipio de nacimiento víctima' },
        { key: 'plan_vida_victima', label: 'Plan de vida víctima' },
        { key: 'resguardo_victima', label: 'Territorio del hecho' },
        { key: 'vereda_victima', label: 'Vereda del hecho' },
        { key: 'agresor', label: 'Agresor' },
        { key: 'grupo_responsable', label: 'Grupo presunto responsable' },
        { key: 'creado_por', label: 'Creado por:' },
        { key: 'actualizado_por', label: 'Actualizado por:' },
        { key: 'fecha_creacion', label: 'Fecha de creación' },
    ])
    constructor(
        private fb: FormBuilder,
        private dialogRef: MatDialogRef<ExportIncidentComponent>,
        private incidentService: IncidentService,
        private toastr: ToastrService,
        private datePipe: DatePipe,
        private loadingService: LoadingService,
        private adapter: DateAdapter<Date>
    ) {
        this.adapter.setLocale('es-CO')
    }

    ngOnInit(): void {
        this.incidentService.asyncErrorValidation$.subscribe((error) => {
            if (error) {
                this.errorMsg = FormUtils.parseErrorMsgs(error)
            }
        })
    }

    drop(event: CdkDragDrop<IncidentField[]>) {
        this.fields.update((fields) => {
            moveItemInArray(fields, event.previousIndex, event.currentIndex)
            return [...fields]
        })
    }

    remove(field: IncidentField) {
        this.fields.update((fields) =>
            fields.filter((f) => f.key !== field.key)
        )
    }
    closeDialog() {
        this.dialogRef.close()
    }

    exportForm = this.fb.group({
        date_from: ['', [Validators.maxLength(11)]],
        date_to: ['', [Validators.maxLength(11)]],
        columns: [''],
    })

    convertToQueryGet(exportIncident: ExportIncident): QueryGet {
        return {
            search: '',
            limit: 0,
            offset: 0,
            order: '',
            filters: [
                { name: 'columns', value: exportIncident.columns.join(',') },
                { name: 'date_from', value: exportIncident.date_from },
                { name: 'date_to', value: exportIncident.date_to },
            ],
        }
    }

    exportIncident() {
        this.dialogRef.close()
        if (this.exportForm.valid) {
            const dateFrom = this.exportForm.get('date_from')?.value
            const dateTo = this.exportForm.get('date_to')?.value
            const payload: ExportIncident = {
                date_from: dateFrom
                    ? this.datePipe.transform(dateFrom, 'yyyy-MM-dd')!
                    : '',
                date_to: dateTo
                    ? this.datePipe.transform(dateTo, 'yyyy-MM-dd')!
                    : '',
                columns: this.fields().map((field) => field.key),
            }
            this.loadingService.show(true)
            const queryParams = this.convertToQueryGet(payload)
            this.incidentService.exportMadeja(queryParams).subscribe({
                next: (res) => {
                    const response = res as AxiosResponse<Blob>
                    const data = response.data
                    const filename =
                        'content-disposition' in response.headers
                            ? response.headers['content-disposition'].split(
                                  '='
                              )[1]
                            : `sucesos_madeja_${this.date}.xlsx`
                    this.loadingService.show(false)
                    const msg = `Archivo generado CORRECTAMENTE!`
                    this.toastr.success(msg, 'Exitoso!', {
                        progressBar: true,
                        timeOut: 5000,
                    })
                    if (data instanceof Blob || typeof data === 'string') {
                        saveAs(data, filename)
                    }
                },
                error: (err: HttpErrorResponse) => {
                    this.loadingService.show(false)

                    const backendMessage =
                        this.errorMsg ??
                        err.error?.message ??
                        'Error inesperado, por favor intentelo nuevamente.'

                    this.toastr.error('Error: ' + backendMessage, 'Error', {
                        progressBar: true,
                        timeOut: 5000,
                    })
                },
            })
        }
    }
}
